import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, Card, Row, Col, Spin, Typography, App } from "antd";
import { ArrowLeftOutlined, PrinterOutlined } from "@ant-design/icons";
import { useReactToPrint } from "react-to-print";

import { getInstitutionApi } from "../../../../apiservice/setting";
import { getsalesReturnRecordApi } from "../../../../apiservice/sales";

import ReportDom from "../../../../components/common/ReportDom";
import SalesReturnVoucherBody from "../../../SalesReturnVoucher/components/SalesReturnVoucherBody";
import SalesReturnVoucherBodyDesignA from "../../../SalesReturnVoucher/components/SalesReturnVoucherBodyDesignA";
import { useShareDocument } from "../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../components/ShareButton/ShareDocumentButton";

const { Title } = Typography;

const SalesReturnQuickVoucher = () => {
  const { id } = useParams(); // route: /sales-return/quick/:id
  const navigate = useNavigate();
  const { message } = App.useApp();
  const reportRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [institution, setInstitution] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [voucherNo, setVoucherNo] = useState("");
  const [accName, setAccName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");

const {
  shareOpen,
  setShareOpen,
  email,
  setEmail,
  handleEmailShare,
  handleWhatsAppShare,
} = useShareDocument();

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Sales_Return_Voucher_${voucher?.sale_r_voucher_no || "Voucher"}`,
  });

  useEffect(() => {
    const fetchVoucherAndInstitution = async () => {
      try {
        setLoading(true);

        const [instRes, voucherRes] = await Promise.all([
          getInstitutionApi(),
          getsalesReturnRecordApi({ sale_r_id: id, from: "voucher" }),
        ]);

        setInstitution(instRes?.data || null);

        if (voucherRes?.data?.length > 0) {
          setVoucher(voucherRes.data[0]);
        } else {
          message.warning("No sales return voucher found.");
        }
      } catch (err) {
        console.error("Failed to load Sales Return Quick Voucher:", err);
        message.error("Failed to load sales return voucher.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchVoucherAndInstitution();
  }, [id, message]);

  useEffect(() => {
    if (shareOpen && customerEmail) {
      setEmail(customerEmail);
    }
  }, [shareOpen, customerEmail, setEmail]);

  if (loading)
    return (
      <div style={{ textAlign: "center", paddingTop: 100 }}>
        <Spin size="large" />
      </div>
    );

  if (!voucher)
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>No Voucher Found</Title>
        <Button onClick={() => navigate("/sales-dashboard")}>Back</Button>
      </div>
    );

  return (
    <div className="voucher-page-wrapper" style={{ padding: 24, background: "#f5f5f5", minHeight: "100vh" }}>
      {/* Header Bar */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }} gutter={[8, 8]}>
        <Col>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
        </Col>
        <Col>
        <Row gutter={8}>
          <Col>
            <ShareDocumentButton
              disabled={!voucher}
              shareOpen={shareOpen}
              setShareOpen={setShareOpen}
              email={email}
              setEmail={setEmail}
              loading={loading}
              onEmail={() =>
                handleEmailShare({
                  url: `/print/sales-return/${voucher.sale_r_id}`,
                  fileName: `SalesReturn_${
                    voucherNo || voucher.sale_r_voucher_no || "Voucher"
                  }.pdf`,
                  name: accName || "",
                  docNo:
                    voucherNo || voucher.sale_r_voucher_no || "Voucher",
                })
              }
              onWhatsApp={() =>
                handleWhatsAppShare({
                  url: `/print/sales-return/${voucher.sale_r_id}`,
                  fileName: `SalesReturn_${
                    voucherNo || voucher.sale_r_voucher_no || "Voucher"
                  }.pdf`,
                  text: `Hello ${accName || ""}, Your Sales Return ${
                    voucherNo || voucher.sale_r_voucher_no || "Voucher"
                  } is ready. Download here:`,
                })
              }
            />
          </Col>

          <Col>
            <Button
              type="primary"
              icon={<PrinterOutlined />}
              onClick={handlePrint}
            >
              Print Voucher
            </Button>
          </Col>
        </Row>
      </Col>
      </Row>

      {/* Voucher Body */}
      <Card className="shadow-md rounded-xl voucher-wrapper" style={{ padding: 24, background: "white" }}>
        <SalesReturnVoucherBody
          sale_r_id={voucher.sale_r_id}
          setVoucherNo={setVoucherNo}
          setAccName={setAccName}
          setCustomerEmail={setCustomerEmail}          
        />
      </Card>

      {/* Hidden Printable DOM */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          mode={institution?.print_template === "design_a" ? "no-title" : undefined}
          // title="Sales Return Voucher"
          // filters={{
          //   fromDate: voucher.created_date,
          //   toDate: voucher.created_date,
          // }}
          renderTable={() =>
            institution?.print_template === "design_a" ? (
              <SalesReturnVoucherBodyDesignA sale_r_id={voucher.sale_r_id} />
            ) : (
              <SalesReturnVoucherBody
                sale_r_id={voucher.sale_r_id}
                printMode
              />
            )
          }
        />
      </div>
    </div>
  );
};

export default SalesReturnQuickVoucher;
